// fleet-variants.js — which /fleet/<id> pages are duplicate VARIANTS of the
// same helicopter model, and which one is the canonical page for that model.
//
// WHY: the operator runs several airframes of one type (h120, h120_2, h120_3 —
// all "Airbus H120 \"Colibri\""; h135, h135_red, h135_silver — all "Airbus
// H135"). Each got its own indexable /fleet/<id> page carrying the SAME title
// and the same (or empty) marketing copy, so Google saw three near-identical
// thin pages per model competing with each other.
//
// The pages STAY REACHABLE and keep returning 200 — they are linked from the
// fleet grid and the aircraft ids appear throughout pricing. Only two SEO
// signals change: the variants point rel=canonical at the model's main page,
// and they drop out of the sitemap (a sitemap should list canonical URLs).
//
// GROUPING KEY = `name`. There is no explicit model field on the aircraft
// document; `name` is identical across airframes of one model and distinct
// between models, which is exactly the equivalence we need. Deriving it from
// the data rather than hardcoding ids means a fourth H120 inherits the
// behaviour with no code change. `type` would NOT work — it is the coarse class
// ("Light Single-Engine") and would wrongly merge H120 with the Bells.
//
// MAIN PAGE = the shortest id in the group, ties broken alphabetically. For
// both live groups that yields the unsuffixed id (h120 < h120_2 < h120_3;
// h135 < h135_red < h135_silver), which is also the id the fleet grid and the
// glossary treat as the primary airframe.

/** Normalised model key for one aircraft. Null when it cannot be grouped. */
function modelKey(a) {
  const n = a && typeof a.name === 'string' ? a.name.trim().toLowerCase() : '';
  return n || null;
}

/**
 * Map every variant aircraft id to the id of its model's main page.
 * Aircraft that are the only one of their model are absent from the map.
 *
 * @param {Array<{id: string, name?: string}>} fleet
 * @returns {Map<string, string>} variantId → mainId
 */
export function buildVariantCanonicalMap(fleet) {
  const groups = new Map();
  for (const a of Array.isArray(fleet) ? fleet : []) {
    if (!a || !a.id) continue;
    const key = modelKey(a);
    if (!key) continue;                     // unnamed → never grouped
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(String(a.id));
  }
  const map = new Map();
  for (const ids of groups.values()) {
    if (ids.length < 2) continue;           // single airframe → its own canonical
    const main = ids.slice().sort((x, y) => (x.length - y.length) || (x < y ? -1 : x > y ? 1 : 0))[0];
    for (const id of ids) if (id !== main) map.set(id, main);
  }
  return map;
}

/** The canonical /fleet path for an aircraft id, or null when it IS the main page. */
export function variantCanonicalPath(id, fleet) {
  const main = buildVariantCanonicalMap(fleet).get(String(id));
  return main ? '/fleet/' + main : null;
}

/** Ids that are variants (i.e. should be excluded from the sitemap). */
export function variantIds(fleet) {
  return [...buildVariantCanonicalMap(fleet).keys()];
}
